import { cn } from "@/lib/utils";
import { HealicMascot } from "@/components/HealicMascot";
import { PromiseBand } from "@/components/PromiseBand";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";

import { PageShell } from "@/components/PageShell";
import { HeroBookingForm } from "@/components/HeroBookingForm";
import {
  findTreatmentBySlug,
  specialties,
  contact,
  locations,
  treatmentSlug,
} from "@/lib/healic-data";
import { getTreatmentClinicalContent, type ClinicalContent } from "@/lib/condition-content";
import { absUrl, BRAND, breadcrumbJsonLd, surgerySeo } from "@/lib/seo";
import { iconForSymptom, iconForCause } from "@/lib/content-icons";
import {
  ArrowRight,
  Check,
  Phone,
  MapPin,
  Activity,
  AlertCircle,
  Stethoscope,
  HeartPulse,
  ShieldCheck,
  Quote,
  Dna,
  Utensils,
  CigaretteOff,
  Hourglass,
  Thermometer,
  Droplets,
  Zap,
  Moon,
  Frown,
  CircleDot,
  Star,
  Award,
  ChevronDown,
  Scissors,
  PlusCircle,
  Timer,
  Lock,
  ClipboardCheck,
  Wallet,
  ShieldPlus,
} from "lucide-react";
import { useMergedDoctors } from "@/lib/use-merged-doctors";
import { doctorProfileSlug } from "@/lib/old-doctor-slugs";
import { DoctorPortrait } from "@/components/DoctorPortrait";
import surgeryHero from "@/assets/surgery-hero.jpg";
import surgeryProcedure from "@/assets/surgery-procedure.jpg";
import specGeneral from "@/assets/specialty-general-surgery.jpg";
import specGynae from "@/assets/specialty-gynaecology.jpg";
import specOrtho from "@/assets/specialty-orthopaedics.jpg";
import specUro from "@/assets/specialty-urology.jpg";
import specEnt from "@/assets/specialty-ent.jpg";
import specEye from "@/assets/category-hero/ophthalmology.jpeg.asset.json";
import surgeryGallbladder from "@/assets/surgery-gallbladder.jpg";
import treatmentSpine from "@/assets/treatment-spine-joint-correction.jpg";
import surgeryAllDeliveries from "@/assets/surgery-all-deliveries.jpg";
import surgeryNormalDelivery from "@/assets/surgery-normal-delivery.jpg";
import surgeryCSection from "@/assets/surgery-c-section.jpg";
import surgeryDcMtp from "@/assets/surgery-dc-mtp.jpg";
import surgeryOvarianCystectomy from "@/assets/surgery-ovarian-cystectomy.jpg";
import surgeryEndometrialAblation from "@/assets/surgery-endometrial-ablation.jpg";
import surgeryLapGyne from "@/assets/surgery-laparoscopic-gyne.jpg";
import surgeryHysterectomyNew from "@/assets/surgery-hysterectomy-new.jpg";
import surgeryCataract from "@/assets/surgery-cataract.jpg";
import surgeryKeratoplasty from "@/assets/category-hero/keratoplasty.jpeg.asset.json";
import surgeryVitrectomy from "@/assets/category-hero/vitrectomy.jpeg.asset.json";
import surgeryKnee from "@/assets/surgery-knee-replacement.jpg";
import surgeryHip from "@/assets/surgery-hip-replacement.jpg";
import surgeryFracture from "@/assets/surgery-fracture-orif.jpg";
import surgeryArthroscopy from "@/assets/surgery-arthroscopy.jpg";
import surgeryPcnl from "@/assets/surgery-pcnl.jpg";
import surgeryUreteroscopy from "@/assets/surgery-ureteroscopy.jpg";
import surgeryTurp from "@/assets/surgery-turp.jpg";
import surgeryTurbt from "@/assets/surgery-turbt.jpg";
import surgeryCystectomy from "@/assets/surgery-cystectomy.jpg";
import surgeryUrethroplasty from "@/assets/surgery-urethroplasty.jpg";
import surgeryTympanoplasty from "@/assets/surgery-tympanoplasty.jpg";
import surgeryAdenoidectomy from "@/assets/surgery-adenoidectomy.jpg";
import surgerySeptoplasty from "@/assets/surgery-septoplasty.jpg";
import surgeryTonsillectomy from "@/assets/surgery-tonsillectomy.jpg";
import surgeryNasalBoneRepair from "@/assets/surgery-nasal-bone-repair.jpg";
import surgeryMastoidectomy from "@/assets/surgery-mastoidectomy.jpg";
import journey35 from "@/assets/journey/journey-35.jpeg.asset.json";
import journey34 from "@/assets/journey/journey-34.jpeg.asset.json";
import journey33 from "@/assets/journey/journey-33.jpeg.asset.json";
import journey32 from "@/assets/journey/journey-32.jpeg.asset.json";
import journey31 from "@/assets/journey/journey-31.jpeg.asset.json";
import gsHernia from "@/assets/category-hero/hernia.jpeg.asset.json";
import gsPiles from "@/assets/category-hero/piles.jpg.asset.json";
import gsCircumcision from "@/assets/category-hero/circumcision.jpg.asset.json";
import gsVaricose from "@/assets/category-hero/varicose.jpg.asset.json";
import gsLipoma from "@/assets/category-hero/lipoma.jpg.asset.json";
import gsFissure from "@/assets/category-hero/fissure.jpg.asset.json";
import gsFistula from "@/assets/category-hero/fistula.jpg.asset.json";
import gsGynecomastia from "@/assets/category-hero/gynecomastia.jpg.asset.json";
import gyAboutMain from "@/assets/gynecomastia/gynecomastia-about.jpeg.asset.json";

import gsAllDeliveries from "@/assets/category-hero/all-deliveries.jpg.asset.json";
import gyDandc from "@/assets/category-hero/dandc.jpg.asset.json";
import gyOvarianCyst from "@/assets/category-hero/ovarian-cyst.jpg.asset.json";
import gyLapGyne from "@/assets/category-hero/lap-gyne.jpg.asset.json";
import gyHysterectomy from "@/assets/category-hero/hysterectomy.jpg.asset.json";
import gyEndoAblation from "@/assets/category-hero/endometrial-ablation.jpg.asset.json";
import gyEctopic from "@/assets/category-hero/ectopic-pregnancy-banner.jpg.asset.json";
import gyTubalLigation from "@/assets/category-hero/tubal-ligation-banner.jpg.asset.json";

import orthoJointReplacement from "@/assets/category-hero/joint-replacement.jpg.asset.json";
import orthoForearmPlating from "@/assets/category-hero/forearm-plating.jpg.asset.json";
import orthoTendonRepair from "@/assets/category-hero/tendon-repair.jpg.asset.json";
import orthoWoundDebridement from "@/assets/category-hero/wound-debridement.jpg.asset.json";
import orthoCarpalTunnel from "@/assets/category-hero/carpal-tunnel.jpg.asset.json";
import orthoTriggerFinger from "@/assets/category-hero/trigger-finger.jpg.asset.json";
import uroPcnl from "@/assets/category-hero/pcnl.jpg.asset.json";
import uroTurp from "@/assets/category-hero/turp.jpg.asset.json";
import uroTurbt from "@/assets/category-hero/turbt.webp.asset.json";
import uroUrethroplasty from "@/assets/category-hero/urethroplasty.jpg.asset.json";
import entTympanoplasty from "@/assets/category-hero/tympanoplasty.png.asset.json";
import entAdenoidectomy from "@/assets/category-hero/adenoidectomy.jpeg.asset.json";
import entSeptoplasty from "@/assets/category-hero/septoplasty.jpeg.asset.json";
import entTonsillectomy from "@/assets/category-hero/tonsillectomy.jpeg.asset.json";
import entNasalBoneRepair from "@/assets/category-hero/nasal-bone-repair.png.asset.json";
import entMastoidectomy from "@/assets/category-hero/mastoidectomy.jpeg.asset.json";
import entSpecialty from "@/assets/category-hero/ent-specialty.jpeg.asset.json";
import cgVaginoplasty from "@/assets/cosmetic-gyn-cards/Vaginoplasty.jpg.asset.json";
import cgVaginalTightening from "@/assets/cosmetic-gyn-cards/Vaginal_Tightening.webp.asset.json";
import cgStressIncontinence from "@/assets/cosmetic-gyn-cards/Stress_Urinary_Incontinence.jpg.asset.json";
import cgGenitalWarts from "@/assets/cosmetic-gyn-cards/genital_warts.webp.asset.json";
import cgLabiaplasty from "@/assets/cosmetic-gyn-cards/labiaplasty.jpg.asset.json";
import cgVaginalPRP from "@/assets/cosmetic-gyn-cards/Vaginal_PRP.jpg.asset.json";
import cgHymenReconstruction from "@/assets/cosmetic-gyn-cards/Hymen_Reconstruction.jpg.asset.json";
const specialtyImage: Record<string, string> = {
  "general-surgery": specGeneral,
  gynaecology: specGynae,
  "cosmetic-gynaecology": specGynae,
  orthopaedics: specOrtho,
  urology: specUro,
  ent: entSpecialty.url,
  ophthalmology: specEye.url,
};

const treatmentImageOverride: Record<string, string> = {
  "spine-and-joint-correction": treatmentSpine,
  "all-deliveries": gsAllDeliveries.url,
  "all-types-of-delivery": gsAllDeliveries.url,
  "normal-delivery": surgeryNormalDelivery,
  "c-section-delivery": surgeryCSection,
  "dandc-mtp-procedures": gyDandc.url,
  "cataract-surgery": surgeryCataract,
  "full-thickness-penetrating-keratoplasty-corneal-transplant": surgeryKeratoplasty.url,
  vitrectomy: surgeryVitrectomy.url,
  "ovarian-cystectomy": gyOvarianCyst.url,
  "endometrial-ablation": gyEndoAblation.url,
  "laparoscopic-gynecology-surgery": gyLapGyne.url,
  "hysterectomy-surgery": gyHysterectomy.url,
  "ectopic-pregnancy": gyEctopic.url,
  "tubal-ligation": gyTubalLigation.url,
  "joint-replacement": orthoJointReplacement.url,
  "implant-removal": surgeryFracture,
  "forearm-plating": orthoForearmPlating.url,
  "tendon-repair": orthoTendonRepair.url,
  "wound-debridement": orthoWoundDebridement.url,
  "carpal-tunnel-release": orthoCarpalTunnel.url,
  "trigger-finger-surgery": orthoTriggerFinger.url,
  "caudal-block": surgeryHip,
  "pcnl-surgery": uroPcnl.url,
  ureteroscopy: surgeryUreteroscopy,
  "prostate-surgery": uroTurp.url,
  turbt: uroTurbt.url,
  cystectomy: surgeryCystectomy,
  urethroplasty: uroUrethroplasty.url,
  tympanoplasty: entTympanoplasty.url,
  adenoidectomy: entAdenoidectomy.url,
  septoplasty: entSeptoplasty.url,
  tonsillectomy: entTonsillectomy.url,
  "nasal-bone-repair": entNasalBoneRepair.url,
  mastoidectomy: entMastoidectomy.url,
  "total-knee-replacement": surgeryKnee,
  "hip-replacement": surgeryHip,
  "fracture-orif": surgeryFracture,
  arthroscopy: surgeryArthroscopy,
  "hemorrhoids-piles": gsPiles.url,
  "hernia-repair": gsHernia.url,
  circumcision: gsCircumcision.url,
  "varicose-veins": gsVaricose.url,
  "lipoma-removal": gsLipoma.url,
  "anal-fissure": gsFissure.url,
  "anal-fistula": gsFistula.url,
  "gynecomastia-surgery": gsGynecomastia.url,
  vaginoplasty: cgVaginoplasty.url,
  "vaginal-tightening": cgVaginalTightening.url,
  "stress-urinary-incontinence": cgStressIncontinence.url,
  "genital-warts": cgGenitalWarts.url,
  labiaplasty: cgLabiaplasty.url,
  "vaginal-prp": cgVaginalPRP.url,
  "hymen-reconstruction": cgHymenReconstruction.url,
};

export function buildContent(name: string, specialtyName: string, slug: string) {
  const lower = name.toLowerCase();
  const clinical = getTreatmentClinicalContent(slug);
  const fallback: ClinicalContent = {
    short: `Expert ${lower} at Healic with senior ${specialtyName.toLowerCase()} specialists, fixed-price packages and guided recovery.`,
    overview: `${name} at Healic is planned after a detailed specialist consultation, relevant diagnostics and a personalised risk assessment. The care plan focuses on accurate diagnosis, safe treatment selection, transparent pricing and structured follow-up for long-term relief.`,
    symptoms: [
      `Persistent symptoms related to ${lower}`,
      "Pain, swelling or repeated discomfort",
      "Difficulty with daily activity or sleep",
      "Symptoms not improving with medicines",
      "Recurrent infection, bleeding or discharge",
      "Doctor-advised procedure after evaluation",
    ],
    causes: [
      "Underlying tissue, joint or organ changes",
      "Infection, inflammation or blockage",
      "Age-related wear and weakness",
      "Lifestyle, posture or occupational strain",
      "Previous injury or untreated disease",
      "Medical condition confirmed by tests",
    ],
    types: [
      "Medical evaluation",
      "Minimally invasive option",
      "Advanced surgical care",
      "Follow-up recovery plan",
    ],
  };
  const base = clinical ?? fallback;
  const recovery =
    base.recovery ??
    `Recovery after ${lower} depends on the diagnosis and procedure selected. Most patients receive a written discharge plan, medicines, diet or activity advice, follow-up dates and WhatsApp support from the Healic care team.`;
  return {
    ...base,
    short: base.short,
    overview: base.overview,
    benefits: base.benefits ?? [
      "Treated by experienced specialists",
      "Fixed-price package — no hidden charges",
      "Modern equipment & sterile facilities",
      "Insurance & EMI assistance",
      "Pre and post-treatment care included",
      "WhatsApp follow-up support",
    ],
    procedure: base.procedure ?? [
      `Consultation: symptoms, medical history and specialist examination for ${lower}.`,
      `Diagnostics: tests or imaging to confirm causes such as ${base.causes.slice(0, 2).join(" and ").toLowerCase()}.`,
      `Treatment planning: selection from options like ${base.types.slice(0, 3).join(", ").toLowerCase()}.`,
      "Procedure or treatment: performed with appropriate anaesthesia, infection control and safety checks.",
      "Discharge and follow-up: medicines, recovery instructions and review support.",
    ],
    recovery,
    faqs: base.faqs ?? [
      {
        q: `What symptoms mean I may need ${name}?`,
        a: `Common warning signs include ${base.symptoms.slice(0, 4).join(", ").toLowerCase()}. A specialist evaluation confirms whether ${lower} is the right treatment.`,
      },
      {
        q: `What causes the problem treated by ${name}?`,
        a: `Frequent causes include ${base.causes.slice(0, 4).join(", ").toLowerCase()}. Your doctor confirms the exact cause with examination and tests.`,
      },
      {
        q: `Which treatment options are available for ${name}?`,
        a: `Depending on severity, options may include ${base.types.join(", ").toLowerCase()}. Healic recommends the safest option after consultation.`,
      },
      { q: `What is the recovery time after ${lower}?`, a: recovery },
      {
        q: `Is ${name} covered by insurance?`,
        a: "Many medically advised procedures are covered by eligible health insurance policies. Healic supports cashless documentation, pre-authorisation and EMI options where applicable.",
      },
      {
        q: "How do I book a consultation?",
        a: "Call Healic, message on WhatsApp, or fill the booking form on this page and the care team will contact you shortly.",
      },
    ],
  };
}

const causeItems: { icon: typeof Activity; tint: string; text: string }[] = [
  {
    icon: Dna,
    tint: "from-rose-500/15 to-rose-500/0 text-rose-600",
    text: "Genetic or hereditary predisposition",
  },
  {
    icon: Utensils,
    tint: "from-amber-500/15 to-amber-500/0 text-amber-600",
    text: "Lifestyle factors — diet, posture or daily activity",
  },
  {
    icon: HeartPulse,
    tint: "from-emerald-500/15 to-emerald-500/0 text-emerald-600",
    text: "Underlying medical conditions",
  },
  {
    icon: Hourglass,
    tint: "from-sky-500/15 to-sky-500/0 text-sky-600",
    text: "Age-related wear & tear",
  },
  {
    icon: CigaretteOff,
    tint: "from-violet-500/15 to-violet-500/0 text-violet-600",
    text: "Smoking, alcohol or environmental triggers",
  },
  {
    icon: AlertCircle,
    tint: "from-orange-500/15 to-orange-500/0 text-orange-600",
    text: "Previous untreated infections or injuries",
  },
];

const symptomItems: { icon: typeof Activity; text: string }[] = [
  { icon: Thermometer, text: "Persistent pain or discomfort" },
  { icon: CircleDot, text: "Visible swelling or change in tissue" },
  { icon: Frown, text: "Difficulty performing daily activities" },
  { icon: Zap, text: "Sudden flare-ups or sharp episodes" },
  { icon: Moon, text: "Disturbed sleep or constant fatigue" },
  { icon: Droplets, text: "Unusual discharge, bleeding or fever" },
];

const benefitStrip: { icon: typeof Scissors; label: string }[] = [
  { icon: Scissors, label: "Minimally Invasive Options" },
  { icon: PlusCircle, label: "Day-Care Procedure" },
  { icon: Timer, label: "Quick Recovery" },
  { icon: ShieldCheck, label: "Safe & Effective" },
  { icon: Lock, label: "Low Recurrence Risk" },
  { icon: Award, label: "Expert Surgeons" },
];

const journeySteps: { title: string; img: string }[] = [
  { title: "Proper Counselling", img: journey31.url },
  { title: "Fixed Price Explanation", img: journey32.url },
  { title: "Safe Treatment", img: journey33.url },
  { title: "Structured Monitoring", img: journey34.url },
  { title: "Time-Bound Discharge", img: journey35.url },
];

const treatmentTypes: { title: string; img: string }[] = [
  { title: "Open Surgery", img: surgeryHero },
  { title: "Laparoscopic", img: surgeryProcedure },
  { title: "Robotic Assisted", img: specGeneral },
  { title: "Single-Incision", img: surgeryGallbladder },
];

function defaultReviews(name: string) {
  const lower = name.toLowerCase();
  return [
    {
      name: "Priya M.",
      text: `Very transparent pricing and caring staff. Highly recommend Healic for ${lower}.`,
    },
    {
      name: "Rahul S.",
      text: `Got my ${lower} done at Healic. The team was super supportive and recovery was much faster than I expected.`,
    },
    {
      name: "Anil K.",
      text: `Doctors explained everything patiently. The procedure went smoothly and I was discharged on time.`,
    },
    {
      name: "Sunita R.",
      text: `Excellent experience from consultation to discharge. The fixed-price package gave me complete peace of mind.`,
    },
  ];
}

export type TreatmentLoaderData = {
  treatment: ReturnType<typeof findTreatmentBySlug> extends infer T ? Exclude<T, undefined> : never;
  specialty: (typeof specialties)[number];
  related: string[];
  content: ReturnType<typeof buildContent>;
};

export const Route = createFileRoute("/treatments/$slug")({
  beforeLoad: ({ params }) => {
    const treatment = findTreatmentBySlug(params.slug);
    if (treatment?.specialtySlug === "cosmetic-gynaecology") {
      throw redirect({
        to: "/cosmetic-gynecology/$slug",
        params: { slug: treatment.slug },
        statusCode: 301,
      });
    }
  },
  loader: ({ params }) => {
    const treatment = findTreatmentBySlug(params.slug);
    if (!treatment) throw notFound();
    const specialty = specialties.find((s) => s.slug === treatment.specialtySlug)!;
    const related = specialty.treatments.filter((t: string) => t !== treatment.name).slice(0, 4);
    const content = buildContent(treatment.name, specialty.name, treatment.slug);
    return { treatment, specialty, related, content };
  },
  head: ({ loaderData }) => {
    const t = loaderData?.treatment;
    const c = loaderData?.content;
    if (!t || !c) return { meta: [{ title: "Treatment — Healic" }] };
    const path = `/treatments/${t.slug}`;
    const url = absUrl(path);
    const displayName =
      t.slug === "all-types-of-delivery" ? "C-Section and Normal Delivery" : t.name;
    const seo = surgerySeo({
      name: displayName,
      slug: t.slug,
      short: c.short,
      overview: c.overview,
      specialtyName: loaderData?.specialty.name,
    });
    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:title", content: `${seo.shortName} — ${BRAND}` },
        { property: "og:description", content: seo.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: BRAND },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${seo.shortName} — Healic` },
        { name: "twitter:description", content: seo.description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalProcedure",
            name: displayName,
            alternateName: seo.shortName,
            description: c.overview,
            procedureType: "https://schema.org/SurgicalProcedure",
            howPerformed: c.procedure.join(". "),
            followup: c.recovery,
            url,
            relevantSpecialty: { "@type": "MedicalSpecialty", name: loaderData?.specialty.name },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faqs.map((f) => ({
              "@type": "Question",
              name: f.q.replace(/<[^>]+>/g, ""),
              acceptedAnswer: { "@type": "Answer", text: f.a.replace(/<[^>]+>/g, "") },
            })),
          }),
        },
        breadcrumbJsonLd([
          { name: "Home", url: absUrl("/") },
          { name: "Specialties", url: absUrl("/specialties") },
          {
            name: loaderData?.specialty.name ?? "Specialty",
            url: absUrl(`/specialties/${loaderData?.specialty.slug ?? ""}`),
          },
          { name: seo.shortName, url },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Treatment not found</h1>
        <Link to="/specialties" className="mt-6 inline-block text-accent">
          Browse all specialties →
        </Link>
      </div>
    </PageShell>
  ),
  component: () => <TreatmentPage data={Route.useLoaderData()} />,
});

export interface TreatmentPageOverrides {
  /** Override the H1 heading text. */
  heroTitle?: string;
  /** Replace the one-line hero subtext with longer, formatted content. */
  heroBody?: React.ReactNode;
  /** Optional media rendered in the left hero column, above the CTA buttons. */
  heroMedia?: React.ReactNode;
  aboutHeading?: string;
  hideAboutSubtitle?: boolean;
  aboutBody?: React.ReactNode;
  /** Optional CSS class added to the About section wrapper. */
  aboutBackgroundClass?: string;
  /** Hide the About-section image while keeping the text layout. */
  aboutHideImage?: boolean;
  symptomsHeading?: string;
  /** Replace the entire symptoms section with custom content. */
  symptomsSection?: React.ReactNode;
  causesHeading?: string;
  /** Replace the entire causes section with custom content. */
  causesSection?: React.ReactNode;
  methodsHeading?: string;
  methodsIntro?: string;
  /** Replace the entire surgical-methods section with custom content. */
  methodsSection?: React.ReactNode;
  whyChooseHeading?: string;
  /** Replace the entire "Why Choose Healic" section (journey + stats + benefits). */
  whyChooseSection?: React.ReactNode;
  /** Replace the entire patient-stories section. */
  storiesSection?: React.ReactNode;
  /** Replace the patient testimonials shown in the stories section. */
  reviews?: { name: string; text: string }[];
  /** Extra content rendered after the stories section, before the doctors grid. */
  afterStoriesSection?: React.ReactNode;
  /** Descriptive alt text for the main treatment image (also makes it the eager LCP image). */
  heroImageAlt?: string;
  /** Replace the About-section image. */
  heroImageSrc?: string;
  /** Descriptive alt text for the hero background image. */
  heroBgAlt?: string;
  /** Alt text for the four PromiseBand icons, in order. */
  promiseBandAlts?: string[];
  /** Alt text for symptom section images, in order. */
  symptomImageAlts?: string[];
  /** Alt text for causes section images, in order. */
  causeImageAlts?: string[];
  /** Alt text for the Why Choose Healic journey step images, in order. */
  journeyStepAlts?: string[];
  /** Replace the FAQ list entirely. */
  faqs?: { q: string; a: string }[];
  /** Replace the FAQ panel heading. */
  faqHeading?: React.ReactNode;
  /** Content rendered directly below the FAQ accordion. */
  afterFaqSection?: React.ReactNode;
  /** Skip the directory request and doctors grid when a page supplies its own specialist section. */
  hideDoctors?: boolean;
}


export function TreatmentPage({
  data,
  extraSection,
  afterAboutSection,
  beforeAboutSection,
  beforeFaqSection,
  overrides,
}: {
  data: TreatmentLoaderData;
  extraSection?: React.ReactNode;
  afterAboutSection?: React.ReactNode;
  beforeAboutSection?: React.ReactNode;
  beforeFaqSection?: React.ReactNode;
  overrides?: TreatmentPageOverrides;
}) {
  const o = overrides ?? {};
  const { treatment, specialty, related, content } = data;
  const displayName =
    treatment.slug === "all-types-of-delivery" ? "C-Section and Normal Delivery" : treatment.name;
  const allDoctors = useMergedDoctors(!o.hideDoctors);
  const doctors = allDoctors
    .filter((d) => d.specialtySlug === specialty.slug)
    .sort(
      (a, b) =>
        parseInt(String(b.experience ?? "").match(/\d+/)?.[0] ?? "0", 10) -
        parseInt(String(a.experience ?? "").match(/\d+/)?.[0] ?? "0", 10),
    );
  const heroBg =
    treatmentImageOverride[treatment.slug] ?? specialtyImage[specialty.slug] ?? surgeryHero;
  const reviewsList = o.reviews ?? defaultReviews(displayName);

  return (
    <PageShell>
      {/* HERO — compact, matches surgery layout */}
      <section className="relative isolate overflow-hidden bg-primary text-primary-foreground">
        <img
          src={heroBg}
          alt={o.heroBgAlt ?? ""}
          aria-hidden={o.heroBgAlt ? undefined : true}
          fetchPriority="high"
          decoding="async"
          width={780}
          height={520}
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-primary via-primary/85 to-primary/40"
          aria-hidden
        />
        <div className="relative mx-auto max-w-7xl px-4 py-6 lg:px-8 lg:py-8">
          <nav className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-accent">
            <Link to="/specialties" className="hover:opacity-80">
              ← Specialties
            </Link>
            <span className="opacity-50">/</span>
            <Link
              to="/specialties/$slug"
              params={{ slug: specialty.slug }}
              className="hover:opacity-80"
            >
              {specialty.name}
            </Link>
          </nav>
          <div className="mt-3 grid items-start gap-6 lg:grid-cols-[1.35fr_1fr]">
            <div>
              <h1 className="font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
                {o.heroTitle ?? displayName}
              </h1>
              {o.heroBody ? (
                <div className="mt-3 max-w-2xl space-y-3 text-sm opacity-90 lg:text-base">
                  {o.heroBody}
                </div>
              ) : (
                <p className="mt-3 max-w-xl text-sm opacity-90 lg:text-base">{content.short}</p>
              )}

              <div className="mt-5 flex flex-wrap gap-2.5">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-pill bg-accent px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-accent-foreground hover:opacity-90"
                >
                  Book consultation <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <a
                  href={`tel:${contact.phoneTel}`}
                  className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-5 py-2.5 text-xs font-semibold ring-1 ring-white/25 backdrop-blur"
                >
                  <Phone className="h-3.5 w-3.5 text-accent" /> {contact.phone}
                </a>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-4">
                {[
                  { icon: Award, big: "5,000+", small: "Successful Procedures" },
                  { icon: Star, big: "Average 4.8", small: "Patient Rating" },
                  { icon: Stethoscope, big: "85+ NPS", small: "Happy Patients" },
                  { icon: ShieldCheck, big: "Insurance", small: "All Accepted" },
                ].map(({ icon: Icon, big, small }) => (
                  <div key={small} className="flex items-start gap-2">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <div>
                      <p className="font-display text-xs font-bold leading-tight sm:text-sm">
                        {big}
                      </p>
                      <p className="mt-0.5 text-[10px] leading-snug opacity-80">{small}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {[
                  { icon: Wallet, label: "0% EMI Available" },
                  { icon: ShieldPlus, label: "Cashless Insurance" },
                  { icon: Timer, label: "Single Point Customer Support" },
                  { icon: ClipboardCheck, label: "Free Consultation" },
                  { icon: Scissors, label: "Advanced Tech" },
                  { icon: PlusCircle, label: "Pickup & Drop" },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 ring-1 ring-white/15 backdrop-blur"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-accent" />
                    <span className="text-[11px] font-semibold leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              {o.heroMedia}
              <div className={o.heroMedia ? "mt-4" : undefined}>
                <HeroBookingForm tagline={`Get expert care for ${displayName}`} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE BAND */}
      <PromiseBand alts={o.promiseBandAlts} />

      {beforeAboutSection}


      {/* ABOUT */}
      <section
        className={cn(
          "mx-auto max-w-6xl px-4 pb-10 pt-2 lg:px-8 lg:pb-14",
          o.aboutBackgroundClass,
        )}
      >
        <div className="text-center">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">
            {o.aboutHeading ?? `About ${displayName}`}
          </h2>
          {!o.hideAboutSubtitle && (
            <p className="mt-1 text-sm font-semibold text-muted-foreground">({specialty.name})</p>
          )}
        </div>
        <div
          className={cn(
            "mt-8 grid items-stretch gap-8",
            !o.aboutHideImage && "lg:grid-cols-[1.7fr_1fr]",
          )}
        >
          <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
            {o.aboutBody ?? (
              <>
                <p>{content.overview}</p>
                {(content.symptoms.length > 0 || content.causes.length > 0) && (
                  <p>
                    Patients commonly visit for{" "}
                    {content.symptoms.slice(0, 3).join(", ").toLowerCase()}. Our{" "}
                    {specialty.name.toLowerCase()} team checks causes such as{" "}
                    {content.causes.slice(0, 3).join(", ").toLowerCase()} and explains suitable
                    options including {content.types.join(", ").toLowerCase()} with transparent
                    pricing and follow-up support.
                  </p>
                )}
              </>
            )}
          </div>
          {!o.aboutHideImage &&
            (treatment.slug === "gynecomastia-surgery" ? (
              <div className="overflow-hidden rounded-2xl border border-border shadow-soft self-start">
                <img
                  src={gyAboutMain.url}
                  alt="Gynecomastia surgery consultation at Healic Multispeciality Hospital"
                  loading="lazy"
                  width={780}
                  height={520}
                  className="h-full max-h-[340px] w-full object-cover"
                />
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-border shadow-soft self-start">
                <img
                  src={o.heroImageSrc ?? heroBg}
                  alt={o.heroImageAlt ?? `${displayName} at Healic Multispeciality Hospital`}
                  width={780}
                  height={520}
                  loading="lazy"
                  decoding="async"
                  className="h-full max-h-[340px] w-full object-cover"
                />
              </div>
            ))}
        </div>
      </section>


      {afterAboutSection}

      {/* SYMPTOMS */}
      {o.symptomsSection ??
        (content.symptoms.length > 0 && (
          <section className="bg-background">
            <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
              <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
                {o.symptomsHeading ?? "Know the Symptoms"}
              </h2>
              <ul className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {content.symptoms.map((text: string, i: number) => {
                  const { icon: Icon } = iconForSymptom(text, i);
                  const img = content.symptomImages?.[i];
                  return (
                    <li
                      key={text}
                      className="relative flex items-start gap-4 rounded-2xl border border-border bg-card p-4 shadow-soft"
                    >
                      <span className="absolute -left-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-[12px] font-bold text-accent-foreground shadow-soft">
                        {i + 1}
                      </span>
                      {img ? (
                        <span className="flex h-16 w-20 shrink-0 overflow-hidden rounded-xl bg-primary-soft">
                          <img
                            src={img}
                            alt={o.symptomImageAlts?.[i] ?? ""}
                            aria-hidden={o.symptomImageAlts?.[i] ? undefined : true}
                            loading="lazy"
                            width={160}
                            height={128}
                            className="h-full w-full object-cover"
                          />
                        </span>
                      ) : (
                        <span className="flex h-16 w-20 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                          <Icon className="h-7 w-7" />
                        </span>
                      )}
                      <p className="text-sm font-semibold leading-snug text-foreground">{text}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        ))}

      {/* CAUSES */}
      {o.causesSection ??
        (treatment.slug !== "dandc-mtp-procedures" &&
          treatment.slug !== "gynecomastia-surgery" &&
          content.causes.length > 0 && (
            <section className="bg-cream">
              <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
                <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
                  {o.causesHeading ?? `${displayName} Causes`}
                </h2>
                <ul
                  className={`mx-auto mt-10 flex flex-wrap justify-center gap-x-8 gap-y-8 ${treatment.slug === "vaginoplasty" || treatment.slug === "stress-urinary-incontinence" ? "max-w-[820px] [&>li]:w-[170px]" : "max-w-2xl [&>li]:w-[140px]"}`}
                >
                  {content.causes.map((text: string, i: number) => {
                    const c = iconForCause(text, i);
                    const Icon = c.icon;
                    const img = content.causeImages?.[i];
                    const bigger =
                      treatment.slug === "vaginoplasty" ||
                      treatment.slug === "stress-urinary-incontinence";
                    return (
                      <li key={text} className="flex flex-col items-center text-center">
                        <span
                          className={`flex items-center justify-center rounded-full bg-white text-cause-icon ring-1 ring-border ${bigger ? "h-24 w-24" : "h-16 w-16"}`}
                        >
                          {img ? (
                            <img
                              src={img}
                              alt={o.causeImageAlts?.[i] ?? ""}
                              aria-hidden={o.causeImageAlts?.[i] ? undefined : true}
                              loading="lazy"
                              width={128}
                              height={128}
                              className={`${bigger ? "h-16 w-16" : "h-9 w-9"} object-contain cause-icon-image`}
                            />
                          ) : (
                            <Icon
                              className={
                                bigger ? "h-10 w-10 text-cause-icon" : "h-7 w-7 text-cause-icon"
                              }
                            />
                          )}
                        </span>
                        <p className="mt-3 text-[12px] font-bold leading-tight text-foreground">
                          {text}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          ))}

      {/* TYPES */}
      {treatment.slug !== "lipoma-removal" &&
        treatment.slug !== "tubal-ligation" &&
        treatment.slug !== "ectopic-pregnancy" &&
        treatment.specialtySlug !== "cosmetic-gynaecology" && (
          <section className="bg-background">
            <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-14">
              <div className="text-center">
                <h2 className="font-display text-3xl font-bold sm:text-4xl">
                  {treatment.slug === "all-types-of-delivery"
                    ? displayName
                    : treatment.slug === "gynecomastia-surgery"
                      ? "Types of Gynecomastia\u00a0"
                      : treatment.slug === "trigger-finger-surgery" ||
                          treatment.slug === "pcnl-surgery" ||
                          treatment.slug === "cataract-surgery" ||
                          treatment.slug ===
                            "full-thickness-penetrating-keratoplasty-corneal-transplant"
                        ? "Surgical Methods at Healic"
                        : treatment.slug === "caudal-block"
                          ? "Procedural Methods of Caudal Block at Healic"
                          : `Types of ${displayName}`}
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
                  Understanding the type helps our specialists choose the safest, most effective
                  treatment for your condition.
                </p>
              </div>
              <ul className="mx-auto mt-10 flex max-w-6xl flex-wrap justify-center gap-6">
                {content.types.map((title: string, i: number) => {
                  const typeImage =
                    content.typeImages?.[i] ?? treatmentTypes[i % treatmentTypes.length].img;
                  const desc =
                    content.typeDescriptions?.[i] ??
                    `${title} is a recognised form of ${displayName.toLowerCase()}. Our ${specialty.name.toLowerCase()} team evaluates severity, symptoms and your medical history to recommend the most suitable approach — with minimally invasive options, fixed-price packages and structured follow-up.`;
                  return (
                    <li
                      key={title}
                      className="group flex w-full max-w-sm flex-col items-center rounded-3xl border border-border bg-card p-6 text-center shadow-soft transition hover:-translate-y-0.5 hover:shadow-card sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]"
                    >
                      <div className="h-28 w-28 overflow-hidden rounded-full ring-4 ring-primary-soft">
                        <img
                          src={typeImage}
                          alt=""
                          loading="lazy"
                          width={224}
                          height={224}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <h3 className="mt-4 font-display text-base font-bold text-foreground">
                        {title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        )}

      {/* TYPES OF LIPOMA REMOVAL — lipoma-specific removal techniques */}
      {treatment.slug === "lipoma-removal" && (
        <section className="bg-background">
          <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-14">
            <div className="text-center">
              <h2 className="font-display text-3xl font-bold sm:text-4xl">
                Types of Lipoma Removal
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
                Our specialists choose the most suitable lipoma removal technique based on size,
                location, and cosmetic goals.
              </p>
            </div>
            <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  name: "Surgical Excision",
                  desc: "The standard approach where the lipoma is removed through a small incision along with its capsule, ensuring complete removal and very low recurrence.",
                },
                {
                  name: "Minimal Incision Extraction",
                  desc: "A smaller cut is used to extract the fatty lump through a tiny opening, leaving a less visible scar and allowing quicker recovery.",
                },
                {
                  name: "Laser-Assisted Removal",
                  desc: "A focused laser softens and breaks down the fatty tissue before gentle removal, reducing bleeding and supporting faster healing.",
                },
              ].map((m) => (
                <li
                  key={m.name}
                  className="group flex h-full flex-col rounded-3xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card"
                >
                  <h3 className="font-display text-lg font-bold leading-tight text-foreground">
                    {m.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{m.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* SURGICAL METHODS at Healic */}
      {o.methodsSection ??
        (content.methods && content.methods.length > 0 && (
          <section className="bg-cream">
            <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-14">
              <div className="text-center">
                <h2 className="font-display text-3xl font-bold sm:text-4xl">
                  {o.methodsHeading ?? "Surgical Methods at Healic"}
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
                  {o.methodsIntro ??
                    "Different patients need different approaches. Our surgeons choose the right method for your condition, age and recovery goals."}
                </p>
              </div>
              <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {content.methods.map((m: { name: string; desc: string }) => (
                  <li
                    key={m.name}
                    className="group flex h-full flex-col rounded-3xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card"
                  >
                    <h3 className="font-display text-lg font-bold leading-tight text-foreground">
                      {m.name}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">{m.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ))}

      {/* WHY CHOOSE — journey + stats + benefits */}
      {o.whyChooseSection ?? (
        <section className="bg-background">
          <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
            <div className="text-center">
              <h2 className="font-display text-3xl font-bold sm:text-4xl">
                {o.whyChooseHeading ?? (
                  <>
                    Why Choose Healic for
                    <br />
                    {displayName}
                  </>
                )}
              </h2>
              <p className="mt-2 text-sm font-semibold text-muted-foreground">({specialty.name})</p>
            </div>
            <div className="mt-10 rounded-3xl bg-primary-soft/70 px-4 py-8 sm:px-8">
              <ul className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
                {journeySteps.map((step, i) => (
                  <li key={step.title} className="relative flex flex-col items-center text-center">
                    <span className="absolute -top-2 left-1/2 z-10 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-card text-[12px] font-bold text-foreground shadow-soft">
                      {i + 1}
                    </span>
                    <div className="overflow-hidden rounded-2xl bg-card shadow-soft">
                      <img
                        src={step.img}
                        alt={o.journeyStepAlts?.[i] ?? ""}
                        aria-hidden={o.journeyStepAlts?.[i] ? undefined : true}
                        loading="lazy"
                        width={320}
                        height={224}
                        className="h-24 w-full object-cover sm:h-28"
                      />
                    </div>
                    <p className="mt-3 font-display text-[13px] font-bold leading-tight text-foreground">
                      {step.title}
                    </p>
                    {i < journeySteps.length - 1 && (
                      <ArrowRight className="absolute right-[-18px] top-12 hidden h-5 w-5 text-accent lg:block" />
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-10 overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <div className="grid divide-y divide-border bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                <div className="px-5 py-5 text-center">
                  <p className="font-display text-2xl font-bold">500+</p>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">
                    Procedures Every Month
                  </p>
                </div>
                <div className="px-5 py-5 text-center">
                  <p className="font-display text-2xl font-bold">Average 4.8</p>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">
                    Google Rating
                  </p>
                </div>
                <div className="px-5 py-5 text-center">
                  <p className="font-display text-2xl font-bold">85+</p>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">
                    NPS Score
                  </p>
                </div>
              </div>
              <div className="px-5 py-7 sm:px-8">
                <h3 className="text-center font-display text-lg font-bold text-foreground">
                  Key benefits at Healic
                </h3>
                <ul className="mx-auto mt-5 grid max-w-4xl gap-3 sm:grid-cols-2">
                  {content.benefits.map((b: string) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 rounded-xl bg-primary-soft/60 px-4 py-3 transition hover:bg-primary-soft"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className="text-[13px] font-medium text-foreground">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PATIENT STORIES */}
      {o.storiesSection ?? (
        <section className="bg-muted/40">
          <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
            <div className="text-center">
              <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                Patient Stories
              </span>
              <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
                What Our Patients say
              </h2>
            </div>
            <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {reviewsList.map((r) => (
                <li
                  key={r.name}
                  className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-soft"
                >
                  <Quote className="h-5 w-5 text-accent/40" />
                  <div className="mt-2 flex items-center gap-1 text-amber-500">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="h-3.5 w-3.5 fill-current" />
                    ))}
                  </div>
                  <p className="mt-3 flex-1 text-[13px] leading-relaxed text-foreground/80">
                    "{r.text}"
                  </p>
                  <div className="mt-4 flex items-center gap-3 border-t border-border pt-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 font-display text-xs font-semibold text-primary">
                      {r.name
                        .split(" ")
                        .map((p) => p[0])
                        .join("")
                        .slice(0, 2)}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{r.name}</p>
                      <p className="text-[11px] text-muted-foreground">Verified patient</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {o.afterStoriesSection}

      {/* DOCTORS */}
      {!o.hideDoctors && doctors.length > 0 && (
        <section className="bg-background">
          <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
            <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
              Doctors for {displayName}
            </h2>
            <p className="mt-1 text-center text-sm font-semibold text-muted-foreground">
              ({specialty.name})
            </p>
            <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {doctors.map((d) => {
                const loc = locations.find((l) => l.slug === d.locationSlug);
                const profileSlug = doctorProfileSlug(d.slug, d.name);
                return (
                  <li
                    key={d.slug}
                    className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft"
                  >
                    <DoctorPortrait
                      doctor={d}
                      className="aspect-[4/3] overflow-hidden"
                      imgClassName="object-top"
                    />
                    <div className="flex flex-1 flex-col p-4">
                      <p className="font-display text-base font-bold leading-tight text-foreground">
                        {d.name}
                      </p>
                      <p className="mt-1 line-clamp-2 text-[12px] text-muted-foreground">
                        {d.specialty}
                      </p>
                      {d.qualifications && (
                        <p className="mt-1 line-clamp-1 text-[12px] font-semibold text-foreground/80">
                          {d.qualifications}
                        </p>
                      )}
                      {d.experience && (
                        <p className="mt-0.5 text-[12px] text-muted-foreground">
                          {d.experience} experience
                        </p>
                      )}
                      {loc && (
                        <p className="mt-0.5 text-[12px] text-muted-foreground">
                          Healic {loc.city}
                        </p>
                      )}
                      <Link
                        to="/doctors/$slug"
                        params={{ slug: profileSlug }}
                        className="mt-3 block rounded-pill bg-primary px-3 py-2 text-center text-[12px] font-semibold uppercase tracking-wide text-primary-foreground hover:opacity-90"
                      >
                        Consult Now
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {/* RELATED TREATMENTS */}
      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <div className="rounded-3xl border border-border bg-card p-6">
            <h3 className="font-display text-xl font-semibold">Related treatments</h3>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {related.map((r: string) => (
                <li key={r}>
                  <Link
                    to="/treatments/$slug"
                    params={{ slug: treatmentSlug(r) }}
                    className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm font-medium hover:bg-cream"
                  >
                    <span>{r}</span>
                    <ArrowRight className="h-4 w-4 text-accent" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {beforeFaqSection}

      {/* FAQs */}
      {(o.faqs ?? content.faqs).length > 0 && (
        <section className="bg-gradient-to-b from-background via-primary-soft/30 to-background">
          <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-16">
            <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <div className="grid lg:grid-cols-[0.9fr_1.4fr]">
                <div className="relative flex flex-col items-center justify-between gap-6 bg-primary-soft/60 p-6 text-center lg:p-8">
                  <div>
                    <span className="inline-flex items-center rounded-full bg-accent/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent">
                      FAQ
                    </span>
                    <h2 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl">
                      {o.faqHeading ?? (
                        <>
                          Find Answers to
                          <br />
                          Your Questions
                        </>
                      )}
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      We are happy to answer every question you have.
                    </p>
                  </div>
                  <div className="relative flex w-full items-end justify-center">
                    <div className="absolute bottom-0 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-accent/20 blur-2xl" />
                    <HealicMascot
                      size="lg"
                      className="relative !h-56 !w-56 sm:!h-64 sm:!w-64 lg:!h-72 lg:!w-72"
                    />
                  </div>
                </div>

                <div className="p-5 sm:p-7 lg:p-8">
                  <ul className="space-y-3">
                    {(o.faqs ?? content.faqs).map((f: { q: string; a: string }, i: number) => (
                      <li key={f.q}>
                        <details
                          open={i === 0}
                          className="group rounded-2xl border border-border bg-card p-4 open:border-accent/40 open:bg-accent/5"
                        >
                          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-semibold text-foreground">
                            <span className="text-sm sm:text-base">
                              <span className="mr-2 text-accent">{i + 1}.</span>
                              <span dangerouslySetInnerHTML={{ __html: f.q }} />
                            </span>
                            <ChevronDown className="h-4 w-4 shrink-0 text-accent transition-transform group-open:rotate-180" />
                          </summary>
                          <p
                            className="mt-3 text-sm text-muted-foreground [&_strong]:font-semibold [&_strong]:text-foreground"
                            dangerouslySetInnerHTML={{ __html: f.a }}
                          />
                        </details>
                      </li>
                    ))}
                  </ul>
                  {o.afterFaqSection}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      {extraSection}
    </PageShell>
  );
}
